import React, { useCallback, useEffect, useMemo, useState } from "react";
// ABM Pagos varios: alta/edición/anulación (el pago se hace vía lote OP)
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import AddBoxTwoToneIcon from "@mui/icons-material/AddBoxTwoTone";
import BlockIcon from "@mui/icons-material/Block";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  ListItemIcon,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import swal from "sweetalert";
import Navbar from "../../Navbar";
import {
  anularPagoNoProveedor,
  actualizarPagoNoProveedor,
  crearPagoNoProveedor,
  getEmpresas,
  getPagosNoProveedor,
} from "./ApiServices";
import PagosNoProveedorFormDialog from "./PagosNoProveedorFormDialog";
import { CONCEPTOS_OPTIONS, labelConcepto } from "./conceptos";
import {
  ESTADOS_OPTIONS,
  colorEstado,
  esPendiente,
  etiquetaEstado,
  formatMoneda,
  formatearFecha,
  idDeEmpresa,
  itemsListado,
  nombreDeEmpresa,
  usuarioActual,
} from "./utils";

const filtrosIniciales = () => ({
  id_empresa: "",
  concepto: "",
  estado: "",
  fecha_desde: "",
  fecha_hasta: "",
});

const muiSearchTextFieldProps = {
  placeholder: "Buscar..",
  sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
  variant: "outlined",
  color: "primary",
  size: "small",
};

const initialState = {
  density: "compact",
  showGlobalFilter: true,
  pagination: { pageSize: 10, pageIndex: 0 },
};

const tableHeadCellProps = {
  align: "left",
  sx: {
    fontSize: 16,
    color: "white",
    fontWeight: "normal",
    backgroundColor: "#2471A3",
    borderColor: "black",
  },
};

const PagosNoProveedorPage = () => {
  const [empresas, setEmpresas] = useState([]);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciales);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("alta");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const mapaEmpresas = useMemo(() => {
    const map = new Map();
    (empresas || []).forEach((emp) => {
      const id = idDeEmpresa(emp);
      if (id != null) map.set(String(id), nombreDeEmpresa(emp));
    });
    return map;
  }, [empresas]);

  const nombreEmpresaFila = useCallback(
    (row) => {
      const id = row?.id_empresa ?? row?.idempresa;
      if (row?.empresa || row?.nombre_empresa) {
        return row.empresa || row.nombre_empresa;
      }
      if (id == null) return "—";
      return mapaEmpresas.get(String(id)) ?? `Empresa ${id}`;
    },
    [mapaEmpresas]
  );

  const cargarEmpresas = useCallback(async () => {
    setLoadingEmpresas(true);
    try {
      const data = await getEmpresas();
      setEmpresas(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Error al cargar empresas");
    } finally {
      setLoadingEmpresas(false);
    }
  }, []);

  const cargarListado = useCallback(async (f = filtrosAplicados) => {
    setLoading(true);
    setError("");
    try {
      const data = await getPagosNoProveedor({
        id_empresa: f.id_empresa || undefined,
        concepto: f.concepto || undefined,
        estado: f.estado || undefined,
        fecha_desde: f.fecha_desde || undefined,
        fecha_hasta: f.fecha_hasta || undefined,
      });
      setRows(itemsListado(data));
    } catch (err) {
      setRows([]);
      setError(err.message || "Error al cargar pagos");
    } finally {
      setLoading(false);
    }
  }, [filtrosAplicados]);

  useEffect(() => {
    cargarEmpresas();
  }, [cargarEmpresas]);

  useEffect(() => {
    cargarListado();
  }, [cargarListado]);

  const aplicarFiltros = () => {
    setFiltrosAplicados({ ...filtros });
  };

  const limpiarFiltros = () => {
    const vacios = filtrosIniciales();
    setFiltros(vacios);
    setFiltrosAplicados(vacios);
  };

  const abrirAlta = () => {
    setSelected(null);
    setDialogMode("alta");
    setDialogOpen(true);
  };

  const abrirEdicion = useCallback((row) => {
    setSelected(row);
    setDialogMode("edicion");
    setDialogOpen(true);
  }, []);

  const handleSubmitForm = async (body) => {
    setSaving(true);
    try {
      if (dialogMode === "edicion" && selected) {
        const id = selected.id ?? selected.id_pago;
        await actualizarPagoNoProveedor(id, body);
        await swal({
          title: "Actualizado",
          text: "El pago se actualizó correctamente",
          icon: "success",
          button: "OK",
        });
      } else {
        await crearPagoNoProveedor(body);
        await swal({
          title: "Creado",
          text: "El pago se registró correctamente",
          icon: "success",
          button: "OK",
        });
      }
      setDialogOpen(false);
      setSelected(null);
      await cargarListado();
    } catch (err) {
      swal({
        title: "Error",
        text: err.message || "No se pudo guardar",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAnular = useCallback(
    async (row) => {
      const motivo = await swal({
        title: "Anular pago",
        text: "Podés indicar un motivo de anulación (opcional)",
        content: {
          element: "input",
          attributes: {
            placeholder: "Motivo de anulación",
            maxLength: 250,
          },
        },
        buttons: ["Cancelar", "Anular"],
        dangerMode: true,
      });
      if (motivo === null) return;

      try {
        const id = row.id ?? row.id_pago;
        await anularPagoNoProveedor(id, {
          user: usuarioActual(),
          ...(String(motivo || "").trim()
            ? { motivo: String(motivo).trim() }
            : {}),
        });
        await swal({
          title: "Anulado",
          text: "El pago fue anulado",
          icon: "success",
          button: "OK",
        });
        await cargarListado();
      } catch (err) {
        swal({
          title: "Error",
          text: err.message || "No se pudo anular",
          icon: "error",
          button: "OK",
        });
      }
    },
    [cargarListado]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 70,
        Cell: ({ row }) => row.original.id ?? row.original.id_pago ?? "—",
      },
      {
        id: "empresa",
        header: "Empresa",
        size: 160,
        Cell: ({ row }) => nombreEmpresaFila(row.original),
      },
      {
        accessorKey: "concepto",
        header: "Concepto",
        size: 130,
        Cell: ({ cell }) => labelConcepto(cell.getValue()),
      },
      {
        accessorKey: "monto",
        header: "Monto",
        size: 120,
        Cell: ({ cell }) => formatMoneda(cell.getValue()),
      },
      {
        accessorKey: "fecha",
        header: "Fecha",
        size: 110,
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
      },
      {
        accessorKey: "motivo",
        header: "Motivo",
        size: 220,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          if (!v) return "—";
          const text = String(v);
          return text.length > 80 ? `${text.slice(0, 80)}…` : text;
        },
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 120,
        Cell: ({ cell }) => {
          const estado = cell.getValue();
          const color = colorEstado(estado);
          return (
            <Chip
              size="small"
              label={etiquetaEstado(estado)}
              color={color}
              variant={color === "default" ? "outlined" : "filled"}
            />
          );
        },
      },
      {
        accessorKey: "user",
        header: "Usuario",
        size: 110,
        Cell: ({ row }) =>
          row.original.user ||
          row.original.usuario ||
          row.original.user_alta ||
          "—",
      },
    ],
    [nombreEmpresaFila]
  );

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#FAFBFC" }}>
      <Navbar />
      <Container
        maxWidth="xl"
        sx={{ pt: "20px", pb: "100px", px: 2, position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight={700}>
            Pagos varios
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => cargarListado()}
              disabled={loading}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddBoxTwoToneIcon />}
              onClick={abrirAlta}
            >
              Nuevo pago
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={2}
          sx={{
            p: 2,
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
              mb: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="filtro-empresa">Empresa</InputLabel>
              <Select
                labelId="filtro-empresa"
                label="Empresa"
                value={filtros.id_empresa}
                onChange={(e) =>
                  setFiltros((p) => ({ ...p, id_empresa: e.target.value }))
                }
                disabled={loadingEmpresas}
              >
                <MenuItem value="">Todas</MenuItem>
                {empresas.map((emp) => {
                  const id = idDeEmpresa(emp);
                  return (
                    <MenuItem key={String(id)} value={id}>
                      {nombreDeEmpresa(emp)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filtro-concepto">Concepto</InputLabel>
              <Select
                labelId="filtro-concepto"
                label="Concepto"
                value={filtros.concepto}
                onChange={(e) =>
                  setFiltros((p) => ({ ...p, concepto: e.target.value }))
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {CONCEPTOS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="filtro-estado">Estado</InputLabel>
              <Select
                labelId="filtro-estado"
                label="Estado"
                value={filtros.estado}
                onChange={(e) =>
                  setFiltros((p) => ({ ...p, estado: e.target.value }))
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {ESTADOS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Desde"
              type="date"
              size="small"
              value={filtros.fecha_desde}
              onChange={(e) =>
                setFiltros((p) => ({ ...p, fecha_desde: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              label="Hasta"
              type="date"
              size="small"
              value={filtros.fecha_hasta}
              onChange={(e) =>
                setFiltros((p) => ({ ...p, fecha_hasta: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <Button variant="contained" onClick={aplicarFiltros}>
              Filtrar
            </Button>
            <Button variant="text" onClick={limpiarFiltros}>
              Limpiar
            </Button>
          </Box>

          <MaterialReactTable
            columns={columns}
            data={rows}
            state={{ isLoading: loading }}
            initialState={initialState}
            localization={MRT_Localization_ES}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            muiTableHeadCellProps={tableHeadCellProps}
            muiTableBodyCellProps={{
              align: "left",
              sx: { borderColor: "black" },
            }}
            enableColumnResizing
            enableColumnActions={false}
            enableColumnFilters={false}
            enableHiding={false}
            enableDensityToggle={false}
            enableColumnDragging={false}
            enableStickyFooter
            enableGlobalFilter
            enableFullScreenToggle={false}
            layoutMode="grid"
            enableRowActions
            positionActionsColumn="last"
            displayColumnDefOptions={{
              "mrt-row-actions": {
                muiTableBodyCellProps: {
                  onClick: (event) => event.stopPropagation(),
                },
              },
            }}
            renderRowActionMenuItems={({ closeMenu, row }) => {
              if (!esPendiente(row.original)) {
                return [
                  <MenuItem key="sin-acciones" disabled>
                    Sin acciones
                  </MenuItem>,
                ];
              }

              return [
                <MenuItem
                  key="editar"
                  onClick={(e) => {
                    e.stopPropagation();
                    abrirEdicion(row.original);
                    closeMenu();
                  }}
                >
                  <ListItemIcon>
                    <EditOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Editar
                </MenuItem>,
                <MenuItem
                  key="anular"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAnular(row.original);
                    closeMenu();
                  }}
                >
                  <ListItemIcon>
                    <BlockIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  Anular
                </MenuItem>,
              ];
            }}
          />
        </Paper>
      </Container>

      <PagosNoProveedorFormDialog
        open={dialogOpen}
        mode={dialogMode}
        initial={selected}
        empresas={empresas}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          setSelected(null);
        }}
        onSubmit={handleSubmitForm}
      />
    </div>
  );
};

export default PagosNoProveedorPage;
